import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import { CustomerDto } from '../../dtos';
import { AccountStatus } from '../../dtos/enums/AccountStatus';
import { formatCurrency, formatDate } from '../../util';
import { Warning, Error } from '@mui/icons-material';

export interface ICustomerStatusAlertProps {
    customer: CustomerDto;
}

export const CustomerStatusAlert: FC<ICustomerStatusAlertProps> = ({ customer }) => {
    const formattedDate = useMemo(() => {
        if (!customer.accountHighCreditDate) return 'N/A';
        return formatDate(customer.accountHighCreditDate);
    }, [customer.accountHighCreditDate]);

    const severity = useMemo(() => {
        switch (customer.accountStatus) {
            case AccountStatus.CreditMessage:
                return 'warning';
            case AccountStatus.OnHold:
                return 'error';
            default:
                return undefined;
        }
    }, [customer.accountStatus]);

    const iconColor = useMemo(() => {
        switch (customer.accountStatus) {
            case AccountStatus.CreditMessage:
                return '#ff9800';
            case AccountStatus.OnHold:
                return '#d32f2f';
            default:
                return undefined;
        }
    }, [customer.accountStatus]);

    const icon = useMemo(() => {
        switch (customer.accountStatus) {
            case AccountStatus.CreditMessage:
                return <Warning fontSize="small" />;
            case AccountStatus.OnHold:
                return <Error fontSize="small" />;
            default:
                return undefined;
        }
    }, [customer.accountStatus]);

    if (customer.accountStatus === AccountStatus.Normal || !severity || !iconColor || !icon) {
        return null;
    }

    return (
        <Alert 
            severity={severity} 
            icon={
                <Box
                    sx={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '& svg': {
                            fill: iconColor,
                            stroke: 'white',
                            strokeWidth: '3px',
                            paintOrder: 'stroke fill',
                        }
                    }}
                >
                    {icon}
                </Box>
            }
            sx={{ 
                backgroundColor: iconColor,
                color: 'white',
                '& .MuiAlert-icon': {
                    color: iconColor,
                }
            }}
        >
            <AlertTitle sx={{ color: 'white' }}>Customer {customer.accountStatus}</AlertTitle>
            <Typography variant="body2" sx={{ color: 'white' }}>
                    Credit Limit: {formatCurrency(customer.accountCreditLimit)} | High Credit Date: {formattedDate} | High Credit Amount: {formatCurrency(customer.accountHighCreditAmount)}
                </Typography>
                {customer.accountComment && (
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: 'white', 
                            mt: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word'
                        }}
                    >
                        {customer.accountComment}
                    </Typography>
                )}
        </Alert>
    );
};

